import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withLocalize } from 'react-localize-redux';
import { useFormikContext } from 'formik-2';
import { useSelector } from 'react-redux';
import { getPostbackMacroNetworkName } from '../../../selectors/partnerIntegration';
import {
    applyCtvReportingMethodCascade,
    applyObjectivePostbackDefaults,
    applyPlatformAddToActiveRows,
    getCtvReportingMethodForPlatforms,
} from '../../postbackEventsPlatformsUpdates';
import { applyPlatformTemplateDefaults, resetFormDataToTemplate } from '../../platformTemplateDefaults';
import { Toggle } from '../../../components/widgets';
import Select from '../../../components/widgets/Select';
import Checkbox from '../../../components/atoms/Checkbox';
import { ConfirmChangesDialog } from '../../../components/organisms/NavigationBlocker';
import FormSection from '../FormSection';
import FormField from '../FormField';
import FormInnerSection from '../FormInnerSection';
import Input from '../Input';
import {
    ATTRIBUTION_TAB_IDS,
    CAMPAIGN_OBJECTIVE_OPTIONS,
    CLICK_IMPRESSION_REPORTING_METHODS,
    DATA_SCOPE_EVENTS_OPTIONS,
    DATA_SCOPE_INSTALLS_OPTIONS,
    PLATFORM_OPTIONS,
    PLATFORMS,
    PLATFORM_TEMPLATE_OPTIONS,
    TRANSLATE_PREFIX,
} from '../../consts';
import useConfirmDialog from '../../useConfirmDialog';
import { useFormDataUpdater, useTabUpdater } from '../../formUpdaters';
import css from './GeneralTab.css';

const TAB_PREFIX = `${TRANSLATE_PREFIX}.GENERAL_TAB`;

const GeneralTab = ({ translate, readOnly = false }) => {
    const { values } = useFormikContext();
    const updateGeneral = useTabUpdater(ATTRIBUTION_TAB_IDS.GENERAL);
    const updateFormData = useFormDataUpdater();
    const postbackMacroNetworkName = useSelector(getPostbackMacroNetworkName);
    const [showDisablePostbacksDialog, setShowDisablePostbacksDialog] = useState(false);
    const { confirm, dialog } = useConfirmDialog();

    const generalData = values.formData?.[ATTRIBUTION_TAB_IDS.GENERAL] || {};

    const templateOptions = PLATFORM_TEMPLATE_OPTIONS.map(opt => ({
        value: opt.value,
        label: translate(opt.labelKey),
    }));
    const selectedTemplateOption = templateOptions.find(opt => opt.value === generalData.platform_template) || null;

    const receivePostbacks = generalData.receive_postbacks ?? true;
    const selectedPlatforms = generalData.platforms || [];
    const selectedObjectives = generalData.campaign_objectives || [];
    const hasOtherPlatform = selectedPlatforms.includes(PLATFORMS.OTHER);
    const platformsOther = generalData.platforms_other || '';

    const hasAnyValues = obj => {
        if (obj === null || obj === undefined) return false;
        if (typeof obj !== 'object') return true;
        if (Array.isArray(obj)) return obj.length > 0;
        return Object.values(obj).some(v => hasAnyValues(v));
    };

    const clearPostbacksFields = () =>
        updateFormData(prev => ({
            ...prev,
            [ATTRIBUTION_TAB_IDS.GENERAL]: {
                ...(prev[ATTRIBUTION_TAB_IDS.GENERAL] || {}),
                receive_postbacks: false,
                installs_data_scope: undefined,
                events_data_scope: undefined,
            },
            [ATTRIBUTION_TAB_IDS.POSTBACK_OVERALL_SETUP]: undefined,
            [ATTRIBUTION_TAB_IDS.POSTBACK_TEMPLATES]: undefined,
        }));

    const handleReceivePostbacksToggle = e => {
        const { checked } = e.target;
        if (!checked) {
            const postbackData = values.formData?.[ATTRIBUTION_TAB_IDS.POSTBACK_OVERALL_SETUP];
            const templateData = values.formData?.[ATTRIBUTION_TAB_IDS.POSTBACK_TEMPLATES];
            const hasPostbacksData =
                hasAnyValues(postbackData) ||
                hasAnyValues(templateData) ||
                !!generalData.installs_data_scope ||
                !!generalData.events_data_scope;
            if (hasPostbacksData) {
                setShowDisablePostbacksDialog(true);
                return;
            }
            clearPostbacksFields();
            return;
        }
        updateGeneral({ receive_postbacks: checked });
    };

    const handleConfirmDisablePostbacks = () => {
        setShowDisablePostbacksDialog(false);
        clearPostbacksFields();
    };

    const toggleArrayField = (field, value, currentArray) => {
        const next = currentArray.includes(value) ? currentArray.filter(v => v !== value) : [...currentArray, value];
        const isToggleOn = next.includes(value);
        if (field === 'campaign_objectives' && isToggleOn) {
            // Toggle-ON for campaign_objectives may auto-add a postback event row. Use the
            // multi-tab updater so GENERAL + POSTBACK_OVERALL_SETUP + POSTBACK_TEMPLATES change
            // in a single Formik state transition (see useFormDataUpdater rationale in formUpdaters.js).
            updateFormData(prevFormData => {
                const prevGeneral = prevFormData[ATTRIBUTION_TAB_IDS.GENERAL] || {};
                const nextFormData = {
                    ...prevFormData,
                    [ATTRIBUTION_TAB_IDS.GENERAL]: { ...prevGeneral, campaign_objectives: next },
                };
                return applyObjectivePostbackDefaults(nextFormData, value, postbackMacroNetworkName);
            });
            return;
        }
        if (field === 'platforms') {
            // Single pipeline for all platforms toggles. Two dialogs can fire:
            //   - "Clear other platform details?"  — confirm-THEN-edit (gates the platforms change
            //     because dropping "Other" + the typed text is destructive and atomic).
            //   - "Switch click/impression reporting to server-side?" — edit-THEN-confirm: the
            //     platforms change lands immediately, the dialog only decides whether to also flip
            //     the reporting method + clear the elaboration. Cancelling leaves the platforms
            //     change in place with method = BOTH and elaboration intact.
            const clearOther = value === PLATFORMS.OTHER && !next.includes(PLATFORMS.OTHER);

            const currentTracking = values.formData?.[ATTRIBUTION_TAB_IDS.TRACKING_LINK_OPTIONS] || {};
            const currentMethod = currentTracking.click_impression_reporting_method;
            const targetMethod = getCtvReportingMethodForPlatforms(next);
            const hasElaboration = !!currentTracking.click_impression_reporting_method_elaboration?.trim();
            const willClearElaboration =
                targetMethod &&
                targetMethod !== CLICK_IMPRESSION_REPORTING_METHODS.BOTH &&
                currentMethod === CLICK_IMPRESSION_REPORTING_METHODS.BOTH &&
                hasElaboration;

            const applyPlatformsChange = () =>
                updateFormData(prevFormData => {
                    const prevGeneral = prevFormData[ATTRIBUTION_TAB_IDS.GENERAL] || {};
                    const nextGeneral = { ...prevGeneral, platforms: next };
                    if (clearOther) nextGeneral.platforms_other = undefined;
                    const nextFormData = {
                        ...prevFormData,
                        [ATTRIBUTION_TAB_IDS.GENERAL]: nextGeneral,
                    };
                    const withRows = isToggleOn
                        ? applyPlatformAddToActiveRows(nextFormData, value, postbackMacroNetworkName)
                        : nextFormData;
                    // Only apply the CTV cascade silently when there's no elaboration at risk —
                    // otherwise it's deferred to the post-edit dialog so the user can decline.
                    return willClearElaboration ? withRows : applyCtvReportingMethodCascade(withRows);
                });

            const applyCtvCascadeOnly = () => updateFormData(applyCtvReportingMethodCascade);

            const editThenMaybePromptCtv = () => {
                applyPlatformsChange();
                if (!willClearElaboration) return;
                confirm({
                    titleKey: `${TAB_PREFIX}.CONFIRM_DIALOG.CTV_SWITCH_TO_SERVER_SIDE_TITLE`,
                    textKey: `${TAB_PREFIX}.CONFIRM_DIALOG.CTV_SWITCH_TO_SERVER_SIDE_TEXT`,
                    onConfirm: applyCtvCascadeOnly,
                    // No onCancel: the platforms change is already in, method/elaboration stay.
                });
            };

            if (clearOther && platformsOther.trim()) {
                confirm({
                    titleKey: `${TAB_PREFIX}.CONFIRM_DIALOG.CLEAR_PLATFORMS_OTHER_TITLE`,
                    textKey: `${TAB_PREFIX}.CONFIRM_DIALOG.CLEAR_PLATFORMS_OTHER_TEXT`,
                    onConfirm: editThenMaybePromptCtv,
                });
                return;
            }
            editThenMaybePromptCtv();
            return;
        }
        updateGeneral({ [field]: next });
    };

    const installsOptions = DATA_SCOPE_INSTALLS_OPTIONS.map(v => ({
        value: v,
        label: translate(`${TRANSLATE_PREFIX}.DATA_SCOPE.${v.toUpperCase()}`),
    }));

    const eventsOptions = DATA_SCOPE_EVENTS_OPTIONS.map(v => ({
        value: v,
        label: translate(`${TRANSLATE_PREFIX}.DATA_SCOPE.${v.toUpperCase()}`),
    }));

    const selectedInstalls = installsOptions.find(o => o.value === generalData.installs_data_scope) || null;
    const selectedEvents = eventsOptions.find(o => o.value === generalData.events_data_scope) || null;

    const handleTemplateChange = opt => {
        const newTemplate = opt ? opt.value : '';
        const prevTemplate = generalData.platform_template || '';
        if (newTemplate === prevTemplate) return;

        if (prevTemplate === '') {
            // First pick: silent autofill.
            updateFormData(prev => applyPlatformTemplateDefaults(prev, newTemplate, postbackMacroNetworkName));
            return;
        }

        confirm({
            titleKey: `${TAB_PREFIX}.CONFIRM_DIALOG.CHANGE_TEMPLATE_TITLE`,
            textKey: `${TAB_PREFIX}.CONFIRM_DIALOG.CHANGE_TEMPLATE_TEXT`,
            onConfirm: () => updateFormData(() => resetFormDataToTemplate(newTemplate, postbackMacroNetworkName)),
        });
    };

    return (
        <FormSection
            titleKey={`${TRANSLATE_PREFIX}.TABS.GENERAL`}
            descriptionKey={`${TAB_PREFIX}.DESCRIPTION`}
            iconName="settings"
        >
            <FormField
                labelKey={`${TAB_PREFIX}.PLATFORM_TEMPLATE_LABEL`}
                required={!readOnly}
                subtitleKey={`${TAB_PREFIX}.PLATFORM_TEMPLATE_SUBTITLE`}
                anchor="platform_template"
            >
                <Select
                    options={templateOptions}
                    value={selectedTemplateOption}
                    onChange={handleTemplateChange}
                    placeholder={translate(`${TAB_PREFIX}.SELECT_PLACEHOLDER`)}
                    isSearchable={false}
                    isClearable={false}
                    isDisabled={readOnly}
                    className={css.select}
                />
            </FormField>
            {generalData.platform_template && (
                <>
                    <FormInnerSection
                        titleKey={`${TRANSLATE_PREFIX}.CAMPAIGN_TYPES.TITLE`}
                        descriptionKey={`${TRANSLATE_PREFIX}.CAMPAIGN_TYPES.DESCRIPTION`}
                    >
                        <FormField
                            labelKey={`${TRANSLATE_PREFIX}.CAMPAIGN_TYPES.PLATFORMS_LABEL`}
                            required={!readOnly}
                            subtitleKey={`${TRANSLATE_PREFIX}.CAMPAIGN_TYPES.PLATFORMS_SUBTITLE`}
                            fullWidth
                            anchor="platforms"
                        >
                            <div className={css.checkboxGroup}>
                                {PLATFORM_OPTIONS.map(value => {
                                    const isChecked = selectedPlatforms.includes(value);
                                    return (
                                        <Checkbox
                                            key={value}
                                            id={`platform_${value}`}
                                            label={translate(
                                                `${TRANSLATE_PREFIX}.CAMPAIGN_TYPES.PLATFORM_OPTIONS.${value.toUpperCase()}`
                                            )}
                                            checked={isChecked}
                                            onChange={() => toggleArrayField('platforms', value, selectedPlatforms)}
                                            disabled={readOnly}
                                            size="small"
                                            labelClass={isChecked ? css.checkboxLabelActive : css.checkboxLabelInactive}
                                        />
                                    );
                                })}
                            </div>
                            {hasOtherPlatform && (
                                <Input
                                    value={platformsOther}
                                    placeholder={
                                        readOnly
                                            ? undefined
                                            : translate(
                                                  `${TRANSLATE_PREFIX}.CAMPAIGN_TYPES.PLATFORMS_OTHER_PLACEHOLDER`
                                              )
                                    }
                                    onChange={val => updateGeneral({ platforms_other: val })}
                                    disabled={readOnly}
                                    containerClass={css.platformsOtherRow}
                                    required
                                />
                            )}
                        </FormField>
                        <FormField
                            labelKey={`${TRANSLATE_PREFIX}.CAMPAIGN_TYPES.OBJECTIVES_LABEL`}
                            required={!readOnly}
                            subtitleKey={`${TRANSLATE_PREFIX}.CAMPAIGN_TYPES.OBJECTIVES_SUBTITLE`}
                            fullWidth
                            anchor="campaign_objectives"
                        >
                            <div className={css.checkboxGroup}>
                                {CAMPAIGN_OBJECTIVE_OPTIONS.map(value => {
                                    const isChecked = selectedObjectives.includes(value);
                                    return (
                                        <Checkbox
                                            key={value}
                                            id={`objective_${value}`}
                                            label={translate(
                                                `${TRANSLATE_PREFIX}.CAMPAIGN_TYPES.OBJECTIVE_OPTIONS.${value
                                                    .toUpperCase()
                                                    .replace(/-/g, '_')}`
                                            )}
                                            checked={isChecked}
                                            onChange={() =>
                                                toggleArrayField('campaign_objectives', value, selectedObjectives)
                                            }
                                            disabled={readOnly}
                                            size="small"
                                            labelClass={isChecked ? css.checkboxLabelActive : css.checkboxLabelInactive}
                                            labelTooltip={translate(
                                                `${TRANSLATE_PREFIX}.CAMPAIGN_TYPES.OBJECTIVE_TOOLTIPS.${value
                                                    .toUpperCase()
                                                    .replace(/-/g, '_')}`
                                            )}
                                        />
                                    );
                                })}
                            </div>
                        </FormField>
                    </FormInnerSection>
                    <FormInnerSection
                        titleKey={`${TRANSLATE_PREFIX}.DATA_SCOPE.TITLE`}
                        descriptionKey={`${TRANSLATE_PREFIX}.DATA_SCOPE.DESCRIPTION`}
                    >
                        <FormField
                            labelKey={`${TRANSLATE_PREFIX}.DATA_SCOPE.RECEIVE_POSTBACKS_LABEL`}
                            subtitleKey={`${TRANSLATE_PREFIX}.DATA_SCOPE.RECEIVE_POSTBACKS_SUBTITLE`}
                            labelTooltipKey={`${TRANSLATE_PREFIX}.DATA_SCOPE.RECEIVE_POSTBACKS_TOOLTIP`}
                            className={receivePostbacks ? css.activeField : undefined}
                            horizontal
                        >
                            <Toggle
                                checked={receivePostbacks}
                                onToggle={handleReceivePostbacksToggle}
                                disabled={readOnly}
                            />
                        </FormField>
                        <ConfirmChangesDialog
                            open={showDisablePostbacksDialog}
                            title={`${TRANSLATE_PREFIX}.DATA_SCOPE.DISABLE_POSTBACKS_TITLE`}
                            text={`${TRANSLATE_PREFIX}.DATA_SCOPE.DISABLE_POSTBACKS_TEXT`}
                            onAccept={handleConfirmDisablePostbacks}
                            onReject={() => setShowDisablePostbacksDialog(false)}
                        />
                        {receivePostbacks && (
                            <>
                                <FormField
                                    labelKey={`${TRANSLATE_PREFIX}.DATA_SCOPE.INSTALLS_LABEL`}
                                    subtitleKey={`${TRANSLATE_PREFIX}.DATA_SCOPE.INSTALLS_SUBTITLE`}
                                    required={!readOnly}
                                    horizontal
                                    anchor="installs_data_scope"
                                >
                                    <Select
                                        options={installsOptions}
                                        value={selectedInstalls}
                                        onChange={opt => updateGeneral({ installs_data_scope: opt ? opt.value : '' })}
                                        placeholder={translate(`${TAB_PREFIX}.SELECT_PLACEHOLDER`)}
                                        isSearchable={false}
                                        isClearable={false}
                                        isDisabled={readOnly}
                                        className={css.select}
                                    />
                                </FormField>
                                <FormField
                                    labelKey={`${TRANSLATE_PREFIX}.DATA_SCOPE.EVENTS_LABEL`}
                                    subtitleKey={`${TRANSLATE_PREFIX}.DATA_SCOPE.EVENTS_SUBTITLE`}
                                    required={!readOnly}
                                    horizontal
                                    anchor="events_data_scope"
                                >
                                    <Select
                                        options={eventsOptions}
                                        value={selectedEvents}
                                        onChange={opt => updateGeneral({ events_data_scope: opt ? opt.value : '' })}
                                        placeholder={translate(`${TAB_PREFIX}.SELECT_PLACEHOLDER`)}
                                        isSearchable={false}
                                        isClearable={false}
                                        isDisabled={readOnly}
                                        className={css.select}
                                    />
                                </FormField>
                            </>
                        )}
                    </FormInnerSection>
                </>
            )}
            {dialog}
        </FormSection>
    );
};

GeneralTab.propTypes = {
    translate: PropTypes.func.isRequired,
    readOnly: PropTypes.bool,
};

export default withLocalize(GeneralTab);
